import { MasterBuildPart, MasterBuildService } from '@services';
import { useMasterBuildAggregate } from './useMasterBuildAggregate';

export interface IMasterBuildPartsHook {
  loading: boolean;
  dataSource: MasterBuildPart[];
  /**
   * Cost, sale, VAT and the margin between them, summed over every row, so the build can be
   * costed at a glance.
   */
  totals: {
    cost: number;
    net: number;
    vat: number;
    gross: number;
    quantity: number;
    /** Sale net of VAT and discount, less what the parts cost. */
    margin: number;
    /** That margin as a share of the net sale; 0 when nothing has been sold. */
    marginPercent: number;
  };
}

/** Every part and extra on every job of the build, with the totals the drawer prints above them. */
export const useMasterBuildParts = (
  masterBuildId: string | undefined,
  enabled: boolean
): IMasterBuildPartsHook => {
  const { loading, dataSource } = useMasterBuildAggregate<MasterBuildPart>(
    enabled && !!masterBuildId,
    (skipCount, maxResultCount) =>
      MasterBuildService.getParts({ masterBuildId: masterBuildId!, skipCount, maxResultCount }),
    [masterBuildId]
  );

  const summed = dataSource.reduce(
    (acc, p) => ({
      cost: acc.cost + (p.costPrice || 0) * (p.quantity || 0),
      net: acc.net + (p.subTotal || 0),
      vat: acc.vat + (p.vatAmount || 0),
      gross: acc.gross + (p.total || 0),
      quantity: acc.quantity + (p.quantity || 0),
    }),
    { cost: 0, net: 0, vat: 0, gross: 0, quantity: 0 }
  );

  // Against the net sale, not the cost: it is what the garage keeps out of what it charges, which
  // is the number a build is judged on.
  const margin = summed.net - summed.cost;

  return {
    loading,
    dataSource,
    totals: {
      ...summed,
      margin,
      marginPercent: summed.net > 0 ? (margin / summed.net) * 100 : 0,
    },
  };
};
